import { useState } from 'react'
import { FiX, FiUpload, FiPlus } from 'react-icons/fi'
import toast from 'react-hot-toast'

export default function TeamMembersEditor({ formData, setFormData }) {
  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => resolve(reader.result)
      reader.onerror = (error) => reject(error)
    })
  }

  const handleAddTeamMember = () => {
    setFormData(prev => ({
      ...prev,
      content: {
        ...prev.content,
        teamMembers: [
          ...(prev.content.teamMembers || []),
          {
            id: Date.now(),
            name: '',
            position: '',
            profileImage: { url: '', publicId: '' },
            bio: ''
          }
        ]
      }
    }))
    toast.success('New team member added')
  }

  const handleUpdateTeamMember = (memberId, field, value) => {
    setFormData(prev => ({
      ...prev,
      content: {
        ...prev.content,
        teamMembers: (prev.content.teamMembers || []).map(member =>
          member.id === memberId ? { ...member, [field]: value } : member
        )
      }
    }))
  }

  const handleRemoveTeamMember = (memberId) => {
    setFormData(prev => ({
      ...prev,
      content: {
        ...prev.content,
        teamMembers: (prev.content.teamMembers || []).filter(member => member.id !== memberId)
      }
    }))
    toast.success('Team member removed')
  }

  const handleTeamMemberImageUpload = async (memberId, file) => {
    if (!file) return

    // Validate file type
    const validImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml']

    if (!validImageTypes.includes(file.type)) {
      toast.error('Please upload a valid image file (JPG, PNG, GIF, WebP, or SVG)')
      return
    }

    // Validate file size (max 5MB for profile images)
    const maxSize = 5 * 1024 * 1024
    if (file.size > maxSize) {
      toast.error('File size must be less than 5MB')
      return
    }

    try {
      const base64 = await convertFileToBase64(file)

      handleUpdateTeamMember(memberId, 'profileImage', { url: base64, publicId: '' })
      toast.success('Profile image uploaded successfully')
    } catch (error) {
      console.error('Error converting file:', error)
      toast.error('Failed to upload image')
    }
  }

  const handleImageInputChange = (e, memberId) => {
    const file = e.target.files?.[0]
    if (file) {
      handleTeamMemberImageUpload(memberId, file)
    }
  }

  const teamMembers = formData.content.teamMembers || []

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900">Team Members</h3>
        <button
          type="button"
          onClick={handleAddTeamMember}
          className="flex items-center gap-2 px-3 py-2 bg-primary-100 text-primary-700 hover:bg-primary-200 rounded transition-colors font-medium text-sm"
        >
          <FiPlus className="w-4 h-4" />
          Add Member
        </button>
      </div>

      {teamMembers.length === 0 ? (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center text-gray-600">
          <p className="text-sm">No team members added yet. Click "Add Member" to start building your team.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {teamMembers.map((member, idx) => (
            <div key={member.id} className="bg-gray-50 p-4 rounded-lg space-y-3 border border-gray-200 hover:border-primary-300 transition-colors">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <p className="font-medium text-gray-900">Team Member {idx + 1}</p>
                  <p className="text-xs text-gray-500 mt-1">{member.name || 'Name not set'}</p>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveTeamMember(member.id)}
                  className="text-red-600 hover:text-red-900 text-sm font-medium flex items-center gap-1 hover:bg-red-50 px-2 py-1 rounded transition-colors"
                >
                  <FiX className="w-4 h-4" />
                  Remove
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Name Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    value={member.name || ''}
                    onChange={(e) => handleUpdateTeamMember(member.id, 'name', e.target.value)}
                    placeholder="e.g., John Smith"
                    className="input-field w-full"
                    required
                  />
                </div>

                {/* Position Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Position / Role *
                  </label>
                  <input
                    type="text"
                    value={member.position || ''}
                    onChange={(e) => handleUpdateTeamMember(member.id, 'position', e.target.value)}
                    placeholder="e.g., CEO, Designer, Developer"
                    className="input-field w-full"
                    required
                  />
                </div>
              </div>

              {/* Bio/Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bio / Description (Optional)
                </label>
                <textarea
                  value={member.bio || ''}
                  onChange={(e) => handleUpdateTeamMember(member.id, 'bio', e.target.value)}
                  placeholder="Brief description about this team member..."
                  rows="2"
                  className="input-field w-full resize-none"
                />
              </div>

              {/* Profile Image */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Profile Picture
                </label>

                {/* Image Preview */}
                {member.profileImage?.url && (
                  <div className="relative group inline-block">
                    <img
                      src={member.profileImage.url}
                      alt={member.name || 'Profile'}
                      className="h-32 w-32 object-cover rounded-lg border-2 border-primary-300"
                    />
                    <button
                      type="button"
                      onClick={() => handleUpdateTeamMember(member.id, 'profileImage', { url: '', publicId: '' })}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Remove image"
                    >
                      <FiX className="w-4 h-4" />
                    </button>
                  </div>
                )}

                <label className="flex items-center justify-center gap-2 px-4 py-3 bg-blue-50 text-blue-600 hover:bg-blue-100 border border-blue-200 rounded cursor-pointer font-medium text-sm transition-colors">
                  <FiUpload className="w-4 h-4" />
                  {member.profileImage?.url ? 'Change Image' : 'Upload Image'}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageInputChange(e, member.id)}
                    className="hidden"
                  />
                </label>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
